import type { Table } from 'dexie';

import { getDb } from '../../core/db';
import { newId } from '../../core/ids';
import type { Moment, Preset } from '../../core/types';
import { getMomentAccountId } from './accountScope';

export interface CreateMomentInput {
  id?: string;
  preset: Pick<Preset, 'id' | 'name' | 'avatar'>;
  content: string;
  images?: string[];
  createdAt?: number;
  sourceMessageId?: string;
}

function table(): Table<Moment, string> {
  return getDb().moments;
}

export function createMoment(input: CreateMomentInput): Moment {
  return {
    id: input.id ?? newId(),
    presetId: input.preset.id,
    content: input.content,
    images: input.images ?? [],
    createdAt: input.createdAt ?? Date.now(),
    authorName: input.preset.name,
    ...(input.preset.avatar ? { authorAvatar: input.preset.avatar } : {}),
    ...(input.sourceMessageId ? { sourceMessageId: input.sourceMessageId } : {}),
  };
}

export async function createAndSaveMoment(input: CreateMomentInput): Promise<Moment> {
  const moment = createMoment(input);
  await table().put(moment);
  return moment;
}

export async function listMoments(): Promise<Moment[]> {
  const rows = await table().toArray();
  const userId = getMomentAccountId();
  return rows.filter(row => row.userId === undefined || row.userId === userId)
    .sort((a, b) => b.createdAt - a.createdAt || (a.id < b.id ? 1 : -1));
}

export async function deleteMoment(id: string): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.moments, db.momentInteractions, async () => {
    await db.momentInteractions.where('postId').equals(id).delete();
    await table().delete(id);
  });
}

export async function clearMoments(): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.moments, db.momentInteractions, async () => {
    await db.momentInteractions.clear();
    await table().clear();
  });
}
